import { Injectable } from '@angular/core';
import {
    FormGroup,
    FormControl,
    FormArray,
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../../../core/models/form-group-type';
import { IncludeFrequencyForValidationFormModel } from '../../models/include-frequency-for-validation/include-frequency-for-validation-form-model';
import { IncludeFrequencyForValidationEmissionDesignator } from '../../models/include-frequency-for-validation/include-frequency-for-validation-emission-designator';
import { CoordinationParametersFormModel } from '../../models/coordination-parameters-form-model';
import { IncludeFrequencyForValidationResponse } from '../../models/include-frequency-for-validation/include-frequency-for-validation-response';

@Injectable({
    providedIn: 'root',
})
export class IncludeFrequencyForValidationFormService {
    getFormDefinition(): FormGroup<
        NestedFormGroupType<IncludeFrequencyForValidationFormModel>
    > {
        return new FormGroup<
            NestedFormGroupType<IncludeFrequencyForValidationFormModel>
        >({
            frequencyRequestId: new FormControl<number | null>(null),
            includeForValidation: new FormControl<boolean | null>(
                null,
                this.isAtLeastOneFrequencyIncludedForValidationValidator(),
            ),
            isAutocoordApproved: new FormControl<boolean>(false),
            locationNumber: new FormControl<number | null>(null),
            locationTypeCode: new FormControl<string | null>(null),
            antennaNumber: new FormControl<number | null>(null),
            antennaHeightToTip: new FormControl<number | null>(null),
            powerErp: new FormControl<number | null>(null),
            frequencyLow: new FormControl<number | null>(null),
            frequencyHigh: new FormControl<number | null>(null),
            frequencyLabel: new FormControl<string | null>(null),
            emissionDesignatorsListForExport: new FormControl<string | null>(
                null,
            ),
            emissionDesignators: new FormArray<
                FormGroup<
                    FormGroupType<IncludeFrequencyForValidationEmissionDesignator>
                >
            >([]),
        });
    }

    /**
     * Patches the form value with the response. This is necessary because the API response types are different from the form types.
     * @param form
     * @param response
     */
    patchFormValue(
        form: FormArray<
            FormGroup<
                NestedFormGroupType<IncludeFrequencyForValidationFormModel>
            >
        >,
        response: IncludeFrequencyForValidationResponse[],
    ): void {
        form.patchValue(
            response.map((frequency) => {
                return {
                    ...frequency,
                    locationNumber: frequency.scheduleD.number,
                    locationTypeCode: frequency.scheduleD.locationTypeCode,
                    antennaNumber: frequency.antenna.number,
                    antennaHeightToTip: frequency.antenna.heightToTip,
                };
            }),
        );
    }

    /**
     * Will check if at least one frequency is included for validation.
     * If no frequencies are included, it will return an error.
     * @returns
     */
    private isAtLeastOneFrequencyIncludedForValidationValidator(): ValidatorFn {
        return (
            includeForValidationControl: AbstractControl,
        ): ValidationErrors | null => {
            const coordinationParametersFormGroup = includeForValidationControl
                ?.parent?.parent?.parent as FormGroup<
                NestedFormGroupType<CoordinationParametersFormModel>
            >;

            if (!coordinationParametersFormGroup) {
                return null;
            }

            const includeFrequenciesForValidationOptionsFormArray =
                coordinationParametersFormGroup.controls
                    .includeFrequenciesForValidation as FormArray<
                    FormGroup<
                        FormGroupType<IncludeFrequencyForValidationFormModel>
                    >
                >;

            if (
                !includeFrequenciesForValidationOptionsFormArray ||
                !includeFrequenciesForValidationOptionsFormArray.controls
            ) {
                return null;
            }

            const includedFrequencies =
                includeFrequenciesForValidationOptionsFormArray.controls.filter(
                    (control) => control.value.includeForValidation === true,
                );

            if (includedFrequencies.length === 0) {
                return {
                    required:
                        'At least one frequency needs to be included for validation.',
                };
            }

            return null;
        };
    }
}
